import { Stack, Typography, useTheme } from "@mui/material";
import {
	type Control,
	type FieldValues,
	type Path,
	useWatch,
} from "react-hook-form";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { FlatRowFormDatePicker } from "web_myplann/Shared/Components/FlatRow/FlatRowFormDatePicker";
import { DateView } from "web_myplann/Shared/Types/DateView";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";

type BuildingSavingsAdditionalFieldsProps<
	FormType extends FieldValues,
	TContext,
	TTransformed extends FieldValues | undefined,
> = {
	control: Control<FormType, TContext, TTransformed>;
	disableKeyFields?: boolean;
};

export const BuildingSavingsAdditionalFields = <
	FormType extends FieldValues,
	TContext,
	TTransformed extends FieldValues | undefined,
>({
	control,
	disableKeyFields = false,
}: BuildingSavingsAdditionalFieldsProps<FormType, TContext, TTransformed>) => {
	const theme = useTheme();
	const { t } = useResource();
	const signedAt = useWatch({
		control,
		name: "signedAt" as Path<FormType>,
	}) as Date | null;

	return (
		<>
			<Typography variant="sectionLabel">
				{t(Resources.Portfolio.AddBuildingSavings.Sections.additionalInfo)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormDatePicker
					control={control}
					name={"signedAt" as Path<FormType>}
					label={t(Resources.Portfolio.AddBuildingSavings.Fields.signedAt)}
					views={[DateView.Year, DateView.Month, DateView.Day]}
					displayFormat={DateFormat.Display}
					disabled={disableKeyFields}
				/>
				<FlatRowFormDatePicker
					control={control}
					name={"startAt" as Path<FormType>}
					label={t(Resources.Portfolio.AddBuildingSavings.Fields.startAt)}
					views={[DateView.Year, DateView.Month, DateView.Day]}
					displayFormat={DateFormat.Display}
					disabled={disableKeyFields}
				/>
				<FlatRowFormDatePicker
					control={control}
					name={"endAt" as Path<FormType>}
					label={t(Resources.Portfolio.AddBuildingSavings.Fields.endAt)}
					views={[DateView.Year, DateView.Month, DateView.Day]}
					displayFormat={DateFormat.Display}
					minDate={signedAt ?? undefined}
					disabled={disableKeyFields}
					isLast
				/>
			</Stack>
		</>
	);
};
